<script setup lang="ts">
import { ref, watch, reactive, onMounted, Ref, onBeforeUnmount } from 'vue';
import AddressSearch from '@/views/components/AddressSearch.vue';
import CustomChecker from '@/views/components/CustomChecker.vue';
import { searchWorkers } from '@/services/modules/WorkerService.ts';
import Paginator from '@/views/components/Paginator.vue';
// @ts-ignore
import i18n from '@/localization/i18n';
import { isAuth } from '@/utils/functions.ts';
import schema from '@/validation/codeTopelaSchema.ts';
import useChatStore from '@/stores/chat.ts';
import { Pro } from '@/models/Pro.ts';
import { usePageContext } from 'vike-vue/usePageContext'
import { navigate } from 'vike/client/router'
import Link from '@/views/components/Link.vue'
import { defineAsyncComponent } from 'vue';
import RatingStars from '@/views/components/RatingStars.vue';
import RatingBadge from '@/views/components/RatingBadge.vue';

const DefaultVue = defineAsyncComponent(() => import('@/views/layouts/Default.vue'));
const ImageOff = defineAsyncComponent(() => import('vue-material-design-icons/ImageOff.vue'))

const pageContext = usePageContext()

const { data } = pageContext
const listData: any = data

// @ts-ignore
const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL;
const chatStore = useChatStore()
const t = i18n.global.t

const town: Ref<any> = ref(listData?.town ?? null)
const keywords: Ref<any> = ref(null)
const zipcode: Ref<string | null> = ref(listData?.zipcode ?? null)
const department: Ref<any> = ref(listData?.department ?? '00')
const shouldSearchInEntireDepartment = ref(listData?.shouldSearchInEntireDepartment ?? true)
const workersList: Ref<Pro[]> = ref(listData?.workersList ?? [])
const platformCode: Ref<any> = ref(listData?.platformCode ?? '')
const formErrors = reactive({
    code: ''
})
const loading = ref(false)
const pageLoading = ref(listData?.error === false ? false : true)

const count = ref(listData?.count ?? 0)
const currentPage = ref(listData?.currentPage ?? 0)
const totalNumberOfPagesPage = ref(listData?.totalNumberOfPagesPage ?? 0)
const hasSearched = ref(false)
const pageError = ref(false)

const urlParams = ref(listData?.urlParams ?? [])

const searchBarFocused = ref(false)

const config = {
    params: {
        page: pageContext.urlParsed.search.page ?? 1
    }
}

onBeforeUnmount(() => {
    workersList.value = []
    town.value = null
})

const unwatchTown = watch(() => town.value, (newValue) => {
    if (newValue == '') {
        zipcode.value = null
    }
}, { immediate: false })


const unwatchPage = watch(() => pageContext.urlParsed.search.page, (newValue) => {
    if (newValue) {
        loading.value = true
        let config = {
            params: {
                page: newValue
            }
        }

        let payload = {
            "platform_code": platformCode.value ? platformCode.value.toUpperCase() : null,
            "keywords": keywordsArray.value,
            "town": town.value,
            "department": department.value && department.value !== '00' ? department.value : null,
            "zipcode": zipcode.value ? zipcode.value : null
        }

        searchWorkers(payload, config)
            .then((response) => {
                workersList.value = response.data.items.map((item: any) => {
                    return {
                        ...item,
                        address: {
                            town: item.town,
                            zipcode: item.zipcode
                        }
                    }
                })
                count.value = response.data.count
                currentPage.value = response.data.current_page_number
                totalNumberOfPagesPage.value = response.data.total_pages
                loading.value = false
            })
            .catch((err) => {
                loading.value = false
                pageError.value = true
            })

    }
});

const query = reactive({
    keyword: null,
    dep: null,
    town: null,
    code: null,
    zipcode: null
})

function removeParenthesesBlock(address: any) {
    const adr = address.replace(/\s*\(.*?\)\s*/g, '');
    return adr
}


let metaTag: any;
onMounted(() => {
    if (listData?.error === false) {
        pageError.value = false
        pageLoading.value = false
        return
    } else {
        pageLoading.value = true
    }

    let query = {}

    if (pageContext.urlParsed.search.keyword) {
        if (Array.isArray(pageContext.urlParsed.search.keyword)) {
            keywordsArray.value = pageContext.urlParsed.search.keyword;
            tagsList.value = keywordsArray.value
        } else {
            keywordsArray.value.push(pageContext.urlParsed.search.keyword);
            tagsList.value.push(pageContext.urlParsed.search.keyword);
        }
        //query.keyword = keywordsArray.value
        Object.assign(query, { keyword: keywordsArray.value })
    }

    if (pageContext.urlParsed.search.town) {
        town.value = pageContext.urlParsed.search.town
        Object.assign(query, { town: pageContext.urlParsed.search.town })
    }

    if (pageContext.urlParsed.search.dep) {
        department.value = pageContext.urlParsed.search.dep
        Object.assign(query, { dep: pageContext.urlParsed.search.dep })
        shouldSearchInEntireDepartment.value = true
    }

    if (pageContext.urlParsed.search.code) {
        Object.assign(query, { code: pageContext.urlParsed.search.code })
        platformCode.value = pageContext.urlParsed.search.code
    }

    if (pageContext.urlParsed.search.zipcode) {
        zipcode.value = pageContext.urlParsed.search.zipcode
        Object.assign(query, { zipcode: pageContext.urlParsed.search.zipcode })
    }

    const payload = {
        "platform_code": platformCode.value ? platformCode.value : null,
        "keywords": tagsList.value,
        "town": town.value ? removeParenthesesBlock(town.value) : null,
        "department": department.value && department.value !== '00' ? department.value : null,
        "zipcode": zipcode.value ? zipcode.value : null
    }
    searchWorkers(payload, config)
        .then((response) => {
            workersList.value = response.data.items.map((item: any) => {
                return {
                    ...item,
                    address: {
                        town: item.town,
                        zipcode: item.zipcode
                    }
                }
            })
            count.value = response.data.count
            currentPage.value = response.data.current_page_number
            totalNumberOfPagesPage.value = response.data.total_pages
            pageLoading.value = false
        })
        .catch((err) => {
            pageLoading.value = false
            pageError.value = true
        })
})

const keywordsArray: Ref<any> = ref(listData?.tagsList ?? [])
const tagsList: Ref<any> = ref(listData?.tagsList ?? [])

// watch(keywordsArray, (newValue) => {})

// Remove a specific query parameter from the current route
async function removeQueryParam(paramName: any) {
    let query = pageContext.urlParsed.search
    let path = pageContext.urlPathname

    // Remove the specified query parameter
    delete query[paramName];

    let query_string = '?'

    Object.entries(query).forEach((item, index) => {
        if (index == 0) {
            query_string += `${item[0]}=${item[1]}`
        } else {
            query_string += `&${item[0]}=${item[1]}`
        }
    })

    if (query_string == '?') {
        query_string = '/annuaire/search/'
    }

    // Create a new route with the modified query parameters
    await navigate(`${path}${query_string}`)
}

const onSearchByKeyword = async () => {
    loading.value = true
    hasSearched.value = true

    let query = {}

    if (keywords.value) {
        tagsList.value.push(keywords.value)
        keywordsArray.value.push(keywords.value)
        keywords.value = null
    }

    if (tagsList.value.length > 0) {
        // query.keyword = tagsList.value
        Object.assign(query, { query: tagsList.value })
    } else if (urlParams.value.length === 0) {
        removeQueryParam('keyword')
    }

    if (shouldSearchInEntireDepartment.value && town.value) {
        if (zipcode.value) {
            department.value = zipcode.value.toString().substring(0, 2)
            // query.dep = department.value
            Object.assign(query, { dep: department.value })
        }
    } else {
        department.value = null
    }

    if (town.value) {
        Object.assign(query, {
            town: removeParenthesesBlock(town.value)
        })
    }

    if (zipcode.value) {
        Object.assign(query, {
            zipcode: zipcode.value
        })
    }

    if (query) {
        let query_string = '?'
        let defaultSearch = '/annuaire/search/'

        Object.entries(query).forEach((item, index) => {
            if (index == 0) {
                query_string += `${item[0]}=${item[1]}`
            } else {
                query_string += `&${item[0]}=${item[1]}`
            }
        })

        if (query_string == '?') {
            query_string = defaultSearch
        }


        if (urlParams.value.length === 0 || query_string === defaultSearch) {
            urlParams.value = []
            await navigate(`${query_string}`)
        }
    } else {
        await navigate(pageContext.urlPathname)
    }

    const payload = {
        "platform_code": null,
        "keywords": tagsList.value,
        "town": town.value !== null && town.value !== '' ? removeParenthesesBlock(town.value) : null,
        "department": department.value && department.value !== '00' ? department.value : null,
        "zipcode": zipcode.value ? zipcode.value : null
    }

    const config = {
        params: {
            page: 1
        }
    }

    searchWorkers(payload, config)
        .then((response) => {
            workersList.value = response.data.items.map((item: any) => {
                return {
                    ...item,
                    address: {
                        town: item.town,
                        zipcode: item.zipcode
                    }
                }
            })
            count.value = response.data.count
            currentPage.value = response.data.current_page_number
            totalNumberOfPagesPage.value = response.data.total_pages
            loading.value = false
        })
        .catch(() => {
            loading.value = false
            pageError.value = true
        })
}

const onSearchByPlatformCode = () => {
    formErrors.code = ''
    hasSearched.value = true
    schema
        .validate({ code: platformCode.value }, { abortEarly: false })
        .then(async () => {
            loading.value = true
            const payload = {
                "platform_code": platformCode.value.toUpperCase(),
                "keywords": [],
                "town": null,
                "department": null,
                "zipcode": null
            }

            const config = {
                params: {
                    page: 1
                }
            }

            await navigate(`${pageContext.urlPathname}?page=1&code${platformCode.value.toUpperCase()}`)
            searchWorkers(payload, config)
                .then((response) => {
                    workersList.value = response.data.items.map((item: any) => {
                        return {
                            ...item,
                            address: {
                                town: item.town,
                                zipcode: item.zipcode
                            }
                        }
                    })
                    count.value = response.data.count
                    currentPage.value = response.data.current_page_number
                    totalNumberOfPagesPage.value = response.data.total_pages
                    loading.value = false
                })
                .catch(() => {
                    loading.value = false
                    pageError.value = true
                })
        })
        .catch((error) => {
            error.inner.forEach((e: any) => {
                // @ts-ignore
                formErrors[e.path] = e.message;
            });
        })
};

const modalbutton: Ref<any> = ref(null)
const closemodal: Ref<any> = ref(null)
const chatpath = ref('/cs/msg_list')

async function onContact(artisan: Pro) {
    let conversation = {
        "conversation_id": "conversation_" + artisan.account_id,
        "pro": {
            "account_id": artisan.account_id,
            "first_name": artisan.first_name,
            "last_name": artisan.last_name,
            "phone": artisan.phone,
            "company_name": artisan.company_name,
            "rating": artisan.rating,
            "logo": artisan.logo,
            'platform_code': artisan.platform_code,
            "is_active": artisan.is_active
        },
        "last_message": {}
    }

    chatStore.setContactArtisanConvo(conversation)

    if (isAuth()) {
        await navigate(chatpath.value)
    } else {
        modalbutton.value?.click()
    }
}

async function goToLogin() {
    closemodal.value?.click()
    sessionStorage.setItem('destination', chatpath.value)
    await navigate('/auth/login')
}

function containsSeparator(str: string) {
    const separators = [",", ";", " ", "."];

    for (let separator of separators) {
        if (str.includes(separator)) {
            return true;
        }
    }

    return false;
}

const onInput = () => {
    const separators = /[,.; ]+/
    if (keywords.value) {
        if (containsSeparator(keywords.value)) {
            const listKeywords = keywords.value.split(separators)
            const filteredArray = listKeywords.filter((element: any) => element !== "");
            for (let i = 0; i < filteredArray.length; i++) {
                tagsList.value.push(filteredArray[i])
                keywordsArray.value.push(filteredArray[i])
            }
            keywords.value = null
            keywordsArray.value = tagsList.value
        } else {
            keywordsArray.value = [keywords.value]
        }
    }
};

const closeChip = (index: number) => {
    tagsList.value.splice(index, 1)
    //keywordsArray.value = tagsList.value
};

const onSearchBarFocus = () => {
    searchBarFocused.value = true
}

const onSearchBarBlur = () => {
    searchBarFocused.value = false
}

onBeforeUnmount(() => {
    unwatchPage()
    unwatchTown()
    if (metaTag && document.head.contains(metaTag)) {
        document.head.removeChild(metaTag);
    }
})

function formatCommaSeparated(value: string | null | undefined): string {
    if (!value) return "";

    return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== "")
        .join(', ');
}
</script>

<template>
    <DefaultVue>
        <div v-if="!pageLoading && !pageError" class="app-content content margin-from-header p-3"
            style="min-height:unset ; margin-left: 0px">
            <div class="content-overlay"></div>
            <div class="header-navbar-shadow"></div>
            <div class="content-wrapper container-xxl p-0">
                <!-- search header -->
                <section id="multiple-column-form" class="pt-0">
                    <div class="d-none d-sm-none d-md-block d-lg-block d-xl-block">
                        <div class="row d-flex justify-content-center">
                            <div class="col-md-9">
                                <div class="card h-100 bg-white p-3 pb-0">

                                    <div class="card-header border-0">
                                        <h1 style="color:#233648 !important;">
                                            {{ t('w_txt_wkSearch_heading') }} <span v-if="urlParams.length > 0">à {{
                                                urlParams[urlParams.length === 1? 0 : 1] }}</span>
                                        </h1>
                                        <p v-html="t('w_txt_wkSearch_paragraph')"></p>
                                    </div>
                                    <div class="card-body mb-0 pb-0">
                                        <div class="container-sm">
                                            <form class="form" @submit.prevent="(() => { })">
                                                <div class="row">
                                                    <div class="col-md-8 col-12">
                                                        <div class="mb-1">
                                                            <label class="form-label mb-2" for="first-name-column">{{
                                                                t('w_frm_wkSearch_keywords') }}</label>
                                                            <div :class="{ 'searchBarFocus': searchBarFocused }"
                                                                class="form-control text-start mb-0 p-1 ps-2 d-flex"
                                                                style="min-height: 2.5rem;margin-top: -2.5px;">
                                                                <div v-for="(keyword, index) in tagsList"
                                                                    class="btn btn-info btn-sm rounded-pill me-1 d-flex align-items-center">
                                                                    <span style="cursor: default;">{{ keyword }}</span>
                                                                    <span class="ps-2"
                                                                        @click="closeChip(index)"><font-awesome-icon
                                                                            icon="fa-solid fa-xmark" /></span>
                                                                </div>
                                                                <input type="text" v-if="tagsList.length < 3"
                                                                    v-model="keywords" @input="onInput"
                                                                    @focus="onSearchBarFocus" @blur="onSearchBarBlur"
                                                                    :placeholder="tagsList.length > 0 ? '' : t('w_frm_wkSearch_keywordspl')"
                                                                    class="chip-input ms-1"
                                                                    style="border: 0px; flex: 1;">
                                                            </div>
                                                        </div>

                                                    </div>
                                                    <div class="col-md-4 col-12">
                                                        <div class="mb-1">
                                                            <AddressSearch v-model:town="town" v-model:zipcode="zipcode"
                                                                :show-zipcode="true" :use-municipality="true" />
                                                        </div>
                                                    </div>

                                                    <div class="col-12 d-flex justify-content-end mt-3" v-if="false">
                                                        <CustomChecker id="searchInDep"
                                                            v-model:value="shouldSearchInEntireDepartment"
                                                            :input-label="t('w_frm_wkSearch_departments')" />
                                                    </div>

                                                    <div class="col-12 mt-3">
                                                        <div class="row align-items-center">
                                                            <div class="col-md-8 d-flex justify-content-center">
                                                                <div v-if="!loading && hasSearched"
                                                                    style="width: max-content"
                                                                    class="mb-3 mt-3 border border-secondary d-flex align-items-center p-1">
                                                                    <p class="mb-0 fw-bold text-dark">
                                                                        {{ t('w_msg_wkSearch_workersFoundPlural') }} <b
                                                                            style="color: #FEB23C;">{{ count }}</b>
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div class="col-md-4 d-flex justify-content-end">
                                                                <button @click="onSearchByKeyword"
                                                                    style="height: fit-content;"
                                                                    class="btn btn-primary  waves-effect waves-float waves-light on-action-btn">{{
                                                                    t('w_act_search') }}</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3 ">
                                <div class="card h-100 justify-content-center text-center p-3"
                                    style="background-color:#233648;">
                                    <div class="card-header">
                                        <h3 class="text-white">{{ t('w_txt_wkSearch_searchByCodeHeading') }}</h3>
                                    </div>
                                    <div class="card-body d-flex justify-content-center align-items-center ">
                                        <form @submit.prevent="(() => { })">
                                            <div class="input-group" style="height: 4rem;">
                                                <input type="text" id="plateforme-code" v-model="platformCode"
                                                    class="form-control h-100 form-control-lg text-center text-uppercase fs-3 mb-2"
                                                    name="plateforme-code" placeholder="- - - -" maxlength="4">
                                                <button type="submit" @click="onSearchByPlatformCode"
                                                    class="btn btn-info h-100 waves-effect waves-float waves-light">{{
                                                    t('w_act_find') }}</button>
                                            </div>
                                            <p class="mb-0 text-danger fw-bold text-start mt-2" v-if="formErrors.code">
                                                {{ formErrors.code }}</p>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <!-- search result artisan boxes -->
                        </div>
                    </div>

                    <div class="d-block d-sm-block d-md-none d-lg-none d-xl-none">
                        <ul class="nav nav-tabs" id="myTab" role="tablist">
                            <li class="nav-item" role="presentation">
                                <button class="nav-link active" id="home-tab" data-bs-toggle="tab"
                                    data-bs-target="#home" type="button" role="tab" aria-controls="home"
                                    aria-selected="true">
                                    {{ t('w_act_wkSearch_mobile_keywordsTab') }}
                                </button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile"
                                    type="button" role="tab" aria-controls="profile" aria-selected="false">
                                    {{ t('w_act_wkSearch_mobile_CodeTab') }}
                                </button>
                            </li>
                        </ul>

                        <div class="tab-content">
                            <div class="tab-pane active" id="home" role="tabpanel" aria-labelledby="home-tab">
                                <div class="card h-100 bg-white p-3">
                                    <div class="card-header border-0">
                                        <h1 style="color:#233648 !important;">{{ t('w_txt_wkSearch_heading') }}</h1>
                                        <p v-html="t('w_txt_wkSearch_paragraph')"></p>
                                    </div>
                                    <div class="card-body">
                                        <div class="container-sm">
                                            <form class="form" @submit.prevent="(() => { })">
                                                <div class="row">
                                                    <div class="col-md-8 col-12">
                                                        <div class="mb-1">
                                                            <label class="form-label mb-2" for="first-name-column">{{
                                                                t('w_frm_wkSearch_keywords') }}</label>
                                                            <div :class="{ 'searchBarFocus': searchBarFocused }"
                                                                class="form-control text-start mb-0 p-1 ps-2 d-flex "
                                                                style="min-height: 2.5rem; flex-wrap: wrap;">
                                                                <div v-for="(keyword, index) in tagsList"
                                                                    class="btn btn-info btn-sm rounded-pill me-1 mt-1 d-flex align-items-center">
                                                                    <span style="cursor: default;">{{ keyword }}</span>
                                                                    <span class="mx-1">
                                                                        <font-awesome-icon icon="fa-solid fa-xmark"
                                                                            @click="closeChip(index)" />
                                                                    </span>
                                                                </div>
                                                                <input type="text" v-if="tagsList.length < 3"
                                                                    v-model="keywords" @input="onInput"
                                                                    @focus="onSearchBarFocus" @blur="onSearchBarBlur"
                                                                    :placeholder="tagsList.length > 0 ? '' : t('w_frm_wkSearch_keywordspl')"
                                                                    class="chip-input ms-1"
                                                                    style="border: 0px; flex: 1;">
                                                            </div>
                                                        </div>

                                                    </div>
                                                    <div class="col-md-4 col-12">
                                                        <div class="mb-1">
                                                            <AddressSearch v-model:town="town" v-model:zipcode="zipcode"
                                                                :show-zipcode="true" :use-municipality="true" />
                                                        </div>
                                                    </div>

                                                    <div class="col-12 d-flex justify-content-end mt-3" v-if="false">
                                                        <CustomChecker id="searchInDep"
                                                            v-model:value="shouldSearchInEntireDepartment"
                                                            :input-label="t('w_frm_wkSearch_departments')" />
                                                    </div>

                                                    <div class="col-12 d-flex justify-content-end mt-3">
                                                        <button @click="onSearchByKeyword"
                                                            class="btn btn-primary w-100  waves-effect waves-float waves-light">{{
                                                            t('w_act_search') }}</button>
                                                    </div>

                                                    <div v-if="!loading && hasSearched" style="width: max-content"
                                                        class="mb-3 col-12 mt-3 border border-secondary d-flex align-items-center p-1">
                                                        <p class="mb-0 fw-bold text-dark">
                                                            {{ t('w_msg_wkSearch_workersFoundPlural') }} <b
                                                                style="color: #FEB23C;">{{ count }}</b>
                                                        </p>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="tab-pane" id="profile" role="tabpanel" aria-labelledby="profile-tab">
                                <div class="card h-100 justify-content-center text-center p-3"
                                    style="background-color:#233648;">
                                    <div class="card-header">
                                        <h3 class="text-white">{{ t('w_txt_wkSearch_searchByCodeHeading') }}</h3>
                                    </div>
                                    <div class="card-body d-flex justify-content-center align-items-center ">
                                        <form @submit.prevent="(() => { })">
                                            <div class="input-group" style="height: 4rem;">
                                                <input type="text" id="plateforme-code" v-model="platformCode"
                                                    class="form-control h-100 form-control-lg text-center text-uppercase fs-3 mb-2"
                                                    name="plateforme-code" placeholder="- - - -" maxlength="4">
                                                <button type="submit" @click="onSearchByPlatformCode"
                                                    class="btn btn-info h-100 waves-effect waves-float waves-light">{{
                                                    t('w_act_find') }}</button>
                                            </div>
                                            <p class="mb-0 text-danger fw-bold text-start mt-2" v-if="formErrors.code">
                                                {{ formErrors.code }}</p>
                                        </form>
                                    </div>
                                </div>

                                <div v-if="!loading && hasSearched"
                                    class="mb-3 col-12 mt-3 border border-secondary d-flex align-items-center p-1">
                                    <p class="mb-0 fw-bold text-dark">
                                        {{ t('w_msg_wkSearch_workersFoundPlural') }} <b style="color: #FEB23C;">{{ count
                                            }}</b>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div v-if="!loading" class="row match-height row-cols-1 row-cols-md-4 mt-4 p-0">
                        <div v-if="workersList.length > 0" v-for="worker in workersList" class="col-md-3">
                            <div class="card card-height">
                                <div class="card-body pb-0">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <div class="d-flex flex-row">
                                            <div class="avatar-container me-3">
                                                <div v-if="worker.logo" class="me-1 rounded" :style="{
                                                    backgroundImage: 'url(' + worker.logo + ')',
                                                    backgroundSize: 'cover',
                                                    backgroundRepeat: 'no-repeat',
                                                    height: '60px',
                                                    width: '60px',
                                                    backgroundPosition: 'center',
                                                }">
                                                </div>
                                                <div v-else class="avatar avatar-lg me-1"
                                                    style="background-color: transparent ;">
                                                    <image-off :size="70" fillColor="#5e5873" />
                                                </div>
                                                <div v-if="worker.profile_verified"
                                                    :class="{ 'badge-container-no-image': !worker.logo, 'badge-container': worker.logo }">
                                                    <img :src="VITE_ASSET_URL + 'assets/public/assets/img/certif/subscribed-badge.webp'"
                                                        class="badge-img">
                                                </div>

                                            </div>

                                            <div class="user-info mb-4">
                                                <!--                                                <h5 class="mb-0 limit-text">{{ worker.first_name + ' ' + worker.last_name }}</h5>-->
                                                <h5 v-if="worker.company_name" class="mb-0 limit-text">{{
                                                    worker.company_name ?? ' ' }}</h5>
                                                <p v-if="worker.specialty" class="card-text mb-0 specialty">{{
                                                    worker.specialty ?? ' ' }}</p>
                                                <p v-if="worker.address.town" class="card-text mb-0 limit-text">{{
                                                    worker.address.town ? worker.address.town + `
                                                    (${worker.address.zipcode.slice(0, 2)})` : ' ' }}</p>
                                            </div>

                                        </div>

                                    </div>


                                    <p class="card-text description">
                                        {{ worker.description }}
                                    </p>

                                    <p class="card-text">
                                        <strong>Zones d’intervention :</strong>&nbsp;
                                        <span v-if="worker.work_geo_zone">
                                            {{ formatCommaSeparated(worker.work_geo_zone) }}
                                        </span>
                                        <span v-else>
                                            <i>Non définies</i>
                                        </span>
                                    </p>
                                    <p class="text-center">
                                        <Link :href="'/annuaire/' + worker.platform_code"
                                            class="btn btn-outline-secondary text-secondary waves-effect  me-md-1 me-sm-1 me-1 on-see-profile-action-btn">
                                            <i class="bx bx-user"></i>{{ t('w_act_searchPro_seeProfile') }}
                                        </Link>
                                        <button type="button"
                                            class="btn btn-outline-primary waves-effect on-see-profile-action-btn mt-sm-1 my-md-1 my-xl-1 my-sm-1"
                                            @click="onContact(worker)"><i class="bx bx-envelope"></i>{{
                                            t('w_act_contactPro') }}</button>
                                    </p>

                                </div>
                                <div class="d-flex justify-content-center">
                                    <RatingBadge :rating="worker.rating" :google-rating="worker.google_rating" />
                                    <div v-if="worker.is_affiliated" class=""
                                        style="position: absolute; bottom: 25px; right: 5px;">
                                        <img :src="'https://upload.wikimedia.org/wikipedia/commons/a/a4/Leroy_Merlin_-_logo_%28France%2C_1995-%29.svg'"
                                            height="20">
                                    </div>
                                </div>
                                <div class="card-footer text-white p-0 text-center" style="background-color: #233648;">
                                    {{ t('w_txt_wkSearch_topelaCode') }} <strong>{{ worker.platform_code }}</strong>
                                </div>
                            </div>
                        </div>

                        <div v-else class="p-5 my-3 col-md-12">
                            <div class="h-style h4-style use-poppins text-center">{{ t('w_msg_wkSearch_noWorkerFound')
                                }}</div>
                        </div>
                    </div>
                    <div v-else style="height: 40vh; display: flex; justify-content: center; align-items: center;">
                        <div>
                            <vue-loaders-ball-spin-fade-loader color="#FEB23C"
                                scale="1"></vue-loaders-ball-spin-fade-loader>
                        </div>
                    </div>
                </section>
                <!-- Pagination -->
                <div v-if="!loading && totalNumberOfPagesPage > 1" class="row">
                    <div class="col-12">
                        <Paginator :count="count" :currentPage="currentPage" :itemsPerPage="8"
                            :path="'/annuaire/search'" :query="query" />
                    </div>
                </div>
                <!--/ Pagination -->
            </div>
        </div>
        <div v-else-if="pageLoading && !pageError"
            style="height: 90vh; display: flex; justify-content: center; align-items: center;">
            <div>
                <vue-loaders-ball-spin-fade-loader color="#FEB23C" scale="1"></vue-loaders-ball-spin-fade-loader>
            </div>
        </div>
        <div v-else-if="pageError" class="content-wrapper container-xxl p-0" style="margin-top: 100px;">
            <div class="content-body">
                <div class="auth-wrapper auth-basic" style="min-height: calc(var(--vh,1vh)*80);">
                    <div class="w-100 text-center ">
                        <img :src="VITE_ASSET_URL + 'assets/user/assets/img/404.webp'" width="500">
                        <div class="h-style-with-color h1-style use-poppins" style="font-size: 80px;">{{
                            t('w_msg_404_oops') }}</div>
                        <div class="h-style-with-color h3-style use-poppins">{{ t('w_msg_404_page_error') }}</div>
                        <div class="h-style-with-color h3-style use-poppins">{{ t('w_msg_404_page_message') }}</div>
                    </div>
                </div>
            </div>
        </div>

        <button ref="modalbutton" v-show="false" data-bs-toggle="modal" data-bs-target="#verifymobile"></button>
        <div ref="mobileVerificationModalRef" class="modal fade" id="verifymobile" tabindex="-1"
            aria-labelledby="twoFactorAuthSmsTitle" style="display: none;" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg two-factor-auth-sms">
                <div class="modal-content">
                    <div class="modal-header bg-transparent"
                        style="border-bottom:none;padding-top:0px;padding-bottom:0px;">
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="text-center modal-body pb-3 px-sm-5 mx-50">
                        <div class="h-style h4-style use-poppins">{{ t('w_txt_wk_loginAlertHeader') }}</div>
                        <div>
                            <p>{{ t('w_msg_wk_loginAlertMessage') }}</p>


                            <div class="my-5">
                                <button ref="closemodal" data-bs-dismiss="modal" aria-label="Close"
                                    class="btn btn-outline-secondary text-secondary me-2 on-see-profile-action-btn-modal">{{
                                    t('w_act_cancel') }}</button>
                                <button @click="goToLogin" class="btn btn-primary on-see-profile-action-btn-modal">{{
                                    t('w_act_wk_loginAlertMessage') }}</button>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </div>

        <div class="sidenav-overlay"></div>
        <div class="drag-target"></div>
    </DefaultVue>
</template>

<style scoped>
@import url('@/assets/css/bootstrap-extended.css');

.on-see-profile-action-btn-modal {
    padding: 10px 15px !important;
    font-size: 14px !important;
    width: 180px !important;
}

.mapouter {
    position: relative;
    text-align: right;
    width: 100%;
    height: 400px;
}

.gmap_canvas {
    overflow: hidden;
    background: none !important;
    width: 100%;
    height: 400px;
}

.gmap_iframe {
    height: 400px !important;
}

.description {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    /* Number of lines to show */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 4.5rem;
}

.specialty {
    width: 200px;
    /* Set the width of the container */
    white-space: nowrap;
    /* Prevent text from wrapping */
    overflow: hidden;
    /* Hide overflowing content */
    text-overflow: ellipsis;
    /* Add ellipsis when text is truncated */
}

.user-info {
    height: 5rem;
}

.avatar-container {
    position: relative;
}

.badge-container {
    position: relative;
    bottom: -10px;
}

.badge-container-no-image {
    position: relative;
    left: -8px;
    bottom: 0px;
}

.badge-img {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 20px;
    height: auto;
}

.form-check-input:checked {
    background-color: #FEB23C;
    border-color: #FEB23C;
}

.form-control:focus {
    border-color: #FEB23C;
    box-shadow: 0 0 0 0.02rem #FEB23C;
    /* padding-left: 1.5rem; */
}

.form-control {
    text-align: start;
}

.chip-input {
    padding-top: 10px;
    padding-bottom: 10px;
    margin-right: 10px;
}

.chip-input:focus {
    outline: none;
    border: none;
}

.chip-input:focus {
    outline: none;
    border: none;
}

.chip-input::placeholder {
    transition: all 0.2s ease;
    font-size: 16px !important;
}

.chip-input:focus::placeholder {
    transform: translate(5px);
    transition: all 0.2s ease;
}

.searchBarFocus {
    border-color: #FEB23C !important;
    box-shadow: none Im !important;
}

.limit-text {
    width: 200px;
    /* Set the width of the container */
    white-space: nowrap;
    /* Prevent text from wrapping */
    overflow: hidden;
    /* Hide overflowing content */
    text-overflow: ellipsis;
    /* Add ellipsis when text is truncated */
}

p {
    color: #333333
}

.form-label {
    color: black;
    font-size: 14px;
    margin-bottom: 0.3rem;
}
</style>